import { node, icon } from "~/util/dom";
import { _ } from "~/i18n";
import createSDSIcon from "~/ui/icon";
import { MYMAPS_PUBLIC, MYMAPS_STAR_EMPTY } from "~/ui/sds-compatible-icons";
import Tooltip, { ETooltipFlow, ETooltipPosition } from "~/ui/tooltip";
import MyMapsVisited from "~/ui/mymaps/visited";
import { hasVisitedOption, updateMapyBoxItem } from "~/util/mapybox";
import { IMapyBoxItem } from "~/interfaces";

export default class PlacesItem {
	_dataItem: {
		data: IMapyBoxItem & { desc: string };
		imageData: {
			icon: boolean;
			img?: () => HTMLImageElement;
			imgSrc: string;
			svg?: () => HTMLImageElement;
			svgName: string;
			customDesc: string;
		};
		parent: HTMLElement;
	};

	_opts: {
		foreign: boolean;
		setCheckedCounter: () => void;
		draggable: boolean;
		checkbox: boolean;
		showPublic: boolean;
		checked: boolean;
		isPremium?: boolean;
	};

	_output: {
		el: HTMLLIElement;
		title: HTMLHeadingElement;
		desc: HTMLHeadingElement;
		star?: HTMLSpanElement;
		checkbox?: HTMLMapyCheckboxElement;
	};

	_tooltips: Array<Tooltip> = [];

	_hasVisitedOption: boolean;

	_mymapsVisited?: MyMapsVisited;

	_dom: {
		imageCover: HTMLDivElement;
		visitedCover?: HTMLDivElement;
	};

	_tooltipContent: HTMLParagraphElement;

	constructor(dataItem, opts = {}) {
		this._dataItem = dataItem;
		this._tooltips = [];

		const _opts = {
			foreign: false,
			setCheckedCounter: () => {},
			draggable: true,
			checkbox: true,
			showPublic: true,
			checked: false,
			isPremium: false,
		};

		this._opts = Object.assign(_opts, opts);
		this._hasVisitedOption = hasVisitedOption(this._dataItem.data);
		this._mymapsVisited = null;
		this._tooltipContent = node("p");

		this._build();
		this._updateVisitedInImage();
	}

	get data() {
		return this._output;
	}

	get hasVisited() {
		return this._hasVisitedOption;
	}

	get isVisitedCheck() {
		return this._mymapsVisited?.checked;
	}

	set visited(visitedState: boolean) {
		if (this._mymapsVisited) {
			this._mymapsVisited.checked = visitedState;
			this._dataItem.data.visited = visitedState;

			this._updateVisitedInImage();
		}
	}

	closeTooltips() {
		for (const tooltip of this._tooltips) {
			tooltip.hide();
		}
	}

	set subtitle(desc) {
		this._dataItem.data.desc = desc;
		this._output.desc.textContent = this._dataItem.data.desc;
		this._updateTooltipDesc();
	}

	_updateTooltipDesc() {
		this._tooltipContent.innerHTML = `${Mapy.Util.escapeHTML(this._dataItem.data.usertitle ? this._dataItem.data.usertitle : this._dataItem.data.title)}<br/>${Mapy.Util.escapeHTML(this._dataItem.data.desc)}`;
	}

	_build() {
		// item listu
		const el = node("li", {
			className: "item",
		});
		const title = node("h2", { className: "title overflow-ellipsis", textContent: `${this._dataItem.data.usertitle ? this._dataItem.data.usertitle : this._dataItem.data.title}` });
		const desc = node("h3", { className: "desc overflow-ellipsis", textContent: this._dataItem.data.desc });
		const textCover = node("div", { className: "text-cover" });
		const wrapper = node("div");

		this._output = {
			el,
			title,
			desc,
		};

		textCover.appendChild(wrapper);

		const tooltip = new Tooltip({
			anchor: textCover,
			trigger: textCover,
			triggerEventType: "pointerenter",
			content: this._tooltipContent,
			arrow: false,
			flow: ETooltipFlow.START,
			position: ETooltipPosition.TOP,
			offset: [2, 0, 0, 0],
			margin: [0, 0, 0, -10],
			showTimeout: 500,
		});

		this._tooltips.push(tooltip);
		this._updateTooltipDesc();

		el.dataset.id = this._dataItem.data.id;
		// vnitrni cast s obsahem
		const inner = node("div", { className: "inner" });

		el.appendChild(inner);
		// separator, tedy jen cara
		el.appendChild(node("div", { className: "separator" }));

		// handle pro tahani
		if (this._opts.draggable) {
			const points = node("span", { className: "points" });

			points.appendChild(icon("move"));
			inner.appendChild(points);
		}

		// zaskrtavatko pro hromadne akce
		if (this._opts.checkbox) {
			const radio = node("mapy-checkbox");

			radio.checked = this._opts.checked;
			radio.addEventListener("change", () => {
				this._opts.setCheckedCounter();
			});

			inner.appendChild(radio);
			this._output.checkbox = radio;
		}

		// fotka
		const imageCover = node("div", { className: "image-cover" });

		if (this._dataItem.imageData.img) {
			imageCover.appendChild(this._dataItem.imageData.img());

			if (this._dataItem.imageData.icon) {
				imageCover.classList.add("icon");
			}
		} else if (this._dataItem.imageData.svg) {
			imageCover.classList.add("svg");
			imageCover.appendChild(this._dataItem.imageData.svg());
		}

		this._dom = {
			imageCover,
			visitedCover: null,
		};

		inner.appendChild(imageCover);

		// nazev a popis
		wrapper.appendChild(title);
		wrapper.appendChild(desc);
		inner.appendChild(textCover);

		if (this._opts.foreign) { // cizi oblibene
			const star = node("span", { className: "star" });

			star.appendChild(createSDSIcon({ className: "foreign-star", symbol: MYMAPS_STAR_EMPTY }));
			inner.appendChild(star);
			this._output.star = star;
		} else if (this._opts.showPublic) { // akce nad mym oblibenym
			const publicEl = node("span", { className: "public-icon" });

			publicEl.appendChild(createSDSIcon({ className: "icon-public", symbol: MYMAPS_PUBLIC }));
			inner.appendChild(publicEl);

			const tooltip2 = new Tooltip({
				anchor: publicEl,
				trigger: publicEl,
				triggerEventType: "pointerenter",
				content: node("p", { textContent: _("mymap.editSharing") }),
				arrow: false,
				flow: ETooltipFlow.CENTER,
				position: ETooltipPosition.TOP,
				offset: [2, 0, 0, 0],
			});

			this._tooltips.push(tooltip2);

			// tlacitko pro menu
			const opts = node("span", { className: "opts" });

			opts.appendChild(icon("etc-vertical"));
			inner.appendChild(opts);

			if (this._dataItem.data.public) {
				el.classList.add("public");
			}
		}

		if (this._dataItem.data.inactive) {
			wrapper.appendChild(node("h3", { className: "inactive", textContent: this._dataItem.data.temporarilyClosed ? _("detail.content.closed.temporarilyClosed") : _("detail.content.closed.title") }));
			textCover.classList.add("with-inactive");
		}

		if (this._hasVisited()) {
			let reqLock = false;

			this._mymapsVisited = new MyMapsVisited({
				clickable: true,
				checked: this._dataItem.data.visited,
				onClick: async target => {
					if (reqLock) {
						return;
					}

					const newVisited = !target.checked;

					target.checked = newVisited;
					reqLock = true;

					try {
						await updateMapyBoxItem(this._dataItem.data, {
							visited: newVisited,
						});

						this._dataItem.data.visited = newVisited;
						this._updateVisitedInImage();
					} catch (exc) {
						/* eslint-disable-next-line */
						target.checked = !newVisited;
					}

					/* eslint-disable-next-line */
					reqLock = false;
				},
			});
			inner.appendChild(this._mymapsVisited.container);
		}

		this._dataItem.parent = el;
	}

	_hasVisited() {
		return !this._opts.foreign && this._opts.isPremium && this._hasVisitedOption;
	}

	_updateVisitedInImage() {
		if (!this._hasVisited()) {
			return;
		}

		if (this._dom.visitedCover) {
			this._dom.visitedCover.remove();
			this._dom.visitedCover = null;
		}

		// navstiveno na obrazku
		if (this._dataItem.data.visited) {
			const visitedElem = node("div", { className: "visited-cover" });

			visitedElem.append(new MyMapsVisited({
				checked: true,
			}).container);
			this._dom.imageCover.appendChild(visitedElem);
			this._dom.visitedCover = visitedElem;
		}
	}
}
